<div class="mb-6 flex items-start justify-between gap-4">
	<div class="flex items-center gap-3">
		<div class="text-base font-bold text-black">{{ pageTitle }}</div>
		@if (isEdit) {
			<span class="rounded bg-[#E8EEF9] px-2 py-1 text-xs font-medium text-violet">{{ typeBadgeLabel }}</span>
		}
	</div>
	<div class="text-sm text-red">
		<a class="cursor-pointer hover:underline" (click)="cancel()">Feedback Management</a>
		<span class="mx-1">&gt;</span>
		<span class="text-grey-60">{{ breadcrumbLabel }}</span>
	</div>
</div>

@if (templateReady) {
	<form [formGroup]="form" (ngSubmit)="submit()">
		<section class="ts1-form-section mb-6">
			<div class="ts1-form-section__header">Form Details</div>
			<div class="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 md:items-start">
				<div class="form-element">
					<div class="form-element__label-row">
						<mat-label>Title</mat-label>
					</div>
					<mat-form-field appearance="outline" class="form-field form-field-responsive mt-1 w-full">
						<input matInput formControlName="name" placeholder="Pilot Feedback Form" />
						@if (form.get("name")?.hasError("duplicate") && form.get("name")?.touched) {
							<mat-error>{{ form.get("name")?.getError("duplicate") }}</mat-error>
						} @else if (form.get("name")?.invalid && form.get("name")?.touched) {
							<mat-error>This field is required.</mat-error>
						}
					</mat-form-field>
				</div>

				@if (isStandaloneMode) {
					<div class="form-element">
						@if (removedRecipientRoleNotice) {
							<div class="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
								{{ removedRecipientRoleNotice }}
							</div>
						}
						<div class="form-element__label-row">
							<mat-label>Select Recipient</mat-label>
							@if (!isEdit) {
								<div class="standalone-toggle flex items-center gap-2">
									<span class="text-sm font-medium text-grey-60">Standalone form</span>
									<mat-slide-toggle formControlName="isStandalone" color="primary"></mat-slide-toggle>
								</div>
							}
						</div>
						<mat-form-field
							appearance="outline"
							class="feedback-chip-field form-field form-field-responsive mt-1 w-full"
						>
							<mat-select
								formControlName="recipientRoles"
								multiple
								hideSingleSelectionIndicator
								placeholder="Select recipient role"
							>
								<mat-select-trigger>
									<mat-chip-set>
										@for (code of selectedRecipientRoles; track code) {
											<mat-chip (removed)="removeRecipientRole(code)" [removable]="true">
												{{ recipientRoleLabel(code) }}
												<mat-icon matChipRemove>cancel</mat-icon>
											</mat-chip>
										}
									</mat-chip-set>
								</mat-select-trigger>
								@for (option of recipientRoleOptions; track option.value) {
									<mat-option [value]="option.value">{{ option.label }}</mat-option>
								}
							</mat-select>
							@if (form.get("recipientRoles")?.invalid && form.get("recipientRoles")?.touched) {
								<mat-error>Select at least one recipient role.</mat-error>
							}
						</mat-form-field>
					</div>
					<div class="form-element md:col-span-2">
						<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
							<app-cbta-datetime-picker
								label="Start (optional)"
								placeHolder="Select start date & time"
								[datePickerInput]="startsAtControl"
								[showClear]="true"
							></app-cbta-datetime-picker>
							<app-cbta-datetime-picker
								label="End (optional)"
								placeHolder="Select end date & time"
								[datePickerInput]="endsAtControl"
								[minDate]="startsAtControl.value"
								[showClear]="true"
								[errorMessage]="scheduleWindowError ?? ''"
							></app-cbta-datetime-picker>
						</div>
					</div>
				} @else {
					<div class="form-element">
						<div class="form-element__label-row">
							<mat-label>Form Type</mat-label>
							@if (!isEdit) {
								<div class="standalone-toggle flex items-center gap-2">
									<span class="text-sm font-medium text-grey-60">Standalone form</span>
									<mat-slide-toggle formControlName="isStandalone" color="primary"></mat-slide-toggle>
								</div>
							}
						</div>
						<mat-form-field
							appearance="outline"
							class="feedback-chip-field form-field form-field-responsive mt-1 w-full"
						>
							<mat-select
								formControlName="formTypeCodes"
								multiple
								hideSingleSelectionIndicator
								placeholder="Select form type"
							>
								<mat-select-trigger>
									<mat-chip-set>
										@for (code of selectedFormTypeCodes; track code) {
											<mat-chip (removed)="removeFormTypeCode(code)" [removable]="true">
												{{ formTypeLabel(code) }}
												<mat-icon matChipRemove>cancel</mat-icon>
											</mat-chip>
										}
									</mat-chip-set>
								</mat-select-trigger>
								@for (option of filterOptions; track option.value) {
									<mat-option [value]="option.value">{{ formTypeLabel(option.value) }}</mat-option>
								}
							</mat-select>
							@if (form.get("formTypeCodes")?.invalid && form.get("formTypeCodes")?.touched) {
								<mat-error>Select at least one form type.</mat-error>
							}
						</mat-form-field>
					</div>
					<div class="form-element">
						<div class="form-element__label-row">
							<mat-label>Form Name</mat-label>
						</div>
						<mat-form-field
							appearance="outline"
							class="feedback-chip-field form-field form-field-responsive mt-1 w-full"
						>
							<mat-select
								formControlName="formIds"
								multiple
								hideSingleSelectionIndicator
								placeholder="Select existing form"
								[disabled]="!selectedFormTypeCodes.length || loadingForms"
							>
								<mat-select-trigger>
									<mat-chip-set>
										@for (formId of selectedFormIds; track formId) {
											<mat-chip (removed)="removeFormId(formId)" [removable]="true">
												{{ formLabel(formId) }}
												<mat-icon matChipRemove>cancel</mat-icon>
											</mat-chip>
										}
									</mat-chip-set>
								</mat-select-trigger>
								@for (trainingForm of availableForms; track trainingForm.id) {
									<mat-option [value]="trainingForm.id">
										{{ trainingForm.name }}
										@if (selectedFormTypeCodes.length > 1 && trainingForm.formType?.code) {
											<span class="text-grey-60"> — {{ formTypeLabel(trainingForm.formType.code) }}</span>
										}
									</mat-option>
								}
							</mat-select>
							@if (loadingForms) {
								<mat-hint>Loading forms...</mat-hint>
							} @else if (selectedFormTypeCodes.length && availableForms.length === 0) {
								<mat-hint>No forms found for the selected types.</mat-hint>
							}
						</mat-form-field>
					</div>
				}
			</div>
		</section>

		<section class="ts1-form-section mb-6">
			<div class="ts1-form-section__header">Feedback Questions</div>
			<div class="flex flex-col gap-4 p-6" formArrayName="questions">
				<div class="feedback-rating-guide">
					<p class="feedback-rating-guide__title">Rating descriptions</p>
					<ul class="feedback-rating-guide__scale">
						@for (item of ratingScale; track item.score) {
							<li>{{ item.score }} - {{ item.label }}</li>
						}
					</ul>
				</div>
				@for (question of questionGroups; track question; let i = $index) {
					<div class="feedback-question-card" [formGroup]="question">
						<div class="feedback-question-card__header">
							<span class="text-sm font-medium text-grey-60">Question {{ i + 1 }}</span>
							<button
								type="button"
								class="feedback-question-card__remove"
								(click)="removeQuestion(question)"
								[disabled]="questions.length <= 1"
							>
								× Remove
							</button>
						</div>
						<mat-form-field appearance="outline" class="feedback-question-card__prompt form-field w-full">
							<textarea matInput formControlName="prompt" placeholder="Enter question" rows="3"></textarea>
							@if (question.get("prompt")?.invalid && question.get("prompt")?.touched) {
								<mat-error>This field is required.</mat-error>
							}
						</mat-form-field>
						<div class="feedback-question-card__options">
							<div class="feedback-question-card__chip">
								<span>Need rating</span>
								<mat-slide-toggle
									formControlName="required"
									color="primary"
									aria-label="Need rating"
								></mat-slide-toggle>
							</div>
							<div class="feedback-question-card__answer">
								<div class="feedback-question-card__chip">
									<span>Need answer</span>
									<mat-slide-toggle
										formControlName="answerEnabled"
										color="primary"
										aria-label="Need answer"
									></mat-slide-toggle>
								</div>
								<div
									class="feedback-question-card__answer-rule"
									[class.feedback-question-card__answer-rule--off]="!question.get('answerEnabled')?.value"
								>
									<span class="feedback-question-card__answer-rule-label">Answer is</span>
									<mat-radio-group
										class="feedback-question-card__segments"
										formControlName="answerRequired"
										aria-label="Whether the answer is required or optional"
										[attr.aria-disabled]="!question.get('answerEnabled')?.value"
									>
										<mat-radio-button [value]="true" [disabled]="!question.get('answerEnabled')?.value">
											Required
										</mat-radio-button>
										<mat-radio-button [value]="false" [disabled]="!question.get('answerEnabled')?.value">
											Optional
										</mat-radio-button>
									</mat-radio-group>
								</div>
							</div>
							@if (question.hasError("inputRequired")) {
								<p class="feedback-question-card__error">Select a rating, an answer, or both.</p>
							}
						</div>
					</div>
				}
				<button
					type="button"
					class="aa-btn self-start"
					data-variant="tertiary"
					data-size="small"
					(click)="addQuestion()"
					[disabled]="questionLimitReached"
				>
					<span class="aa-mat-icon">add</span>
					Add Question
				</button>
				@if (questions.hasError("maxQuestions")) {
					<p class="feedback-question-card__error">A feedback form can have at most {{ maxQuestions }} questions.</p>
				} @else if (questionLimitReached) {
					<p class="m-0 text-sm text-grey-60">A feedback form can have at most {{ maxQuestions }} questions.</p>
				}
			</div>
		</section>

		<div class="form-actions my-6 flex flex-row gap-4 border-t border-blue-light pt-6">
			<button type="button" class="aa-btn min-w-24" data-variant="tertiary" data-size="small" (click)="cancel()">
				Cancel
			</button>
			<div class="flex-1"></div>
			<button type="submit" class="aa-btn min-w-24" data-variant="primary" data-size="small" [disabled]="form.invalid">
				{{ isEdit ? "Save" : "Create" }}
			</button>
		</div>
	</form>
}
